import { useEffect, useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Check, GripVertical, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

const byPosition = (rows) => [...rows].sort((a, b) => (a.position ?? 1e6) - (b.position ?? 1e6));

// Click the pencil (or the name) to rename a Live issue in place; Enter saves, Esc cancels.
export const RenameTitle = ({ ev, onDone, className = "" }) => {
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(ev.title);
  useEffect(() => setTitle(ev.title), [ev.title]);
  const save = async () => {
    if (title.trim() === ev.title) return setEdit(false);
    try { await api.patch(`/admin/live/${ev.id}`, { title }); toast.success("Renamed"); setEdit(false); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!edit) return (
    <button type="button" onClick={() => setEdit(true)} className={`group flex min-w-0 items-center gap-2 text-left ${className}`} data-testid={`live-rename-open-${ev.id}`}>
      <span className="truncate" data-testid={`live-title-${ev.id}`}>{ev.title}</span><Pencil size={14} className="shrink-0 text-slate-400 group-hover:text-slate-700" /></button>);
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <Input autoFocus value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setTitle(ev.title); setEdit(false); } }}
        className="h-8" data-testid={`live-rename-input-${ev.id}`} />
      <button type="button" onClick={save} disabled={!title.trim()} className="rounded-full p-1.5 text-emerald-700 hover:bg-emerald-50" aria-label="Save name" data-testid={`live-rename-save-${ev.id}`}><Check size={16} /></button>
      <button type="button" onClick={() => { setTitle(ev.title); setEdit(false); }} className="rounded-full p-1.5 text-slate-500 hover:bg-black/5" aria-label="Cancel" data-testid={`live-rename-cancel-${ev.id}`}><X size={16} /></button>
    </div>);
};

const Row = ({ ev, n, onDone }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ev.id });
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`live-order-row-${ev.id}`}
      className={`flex items-center gap-2 bg-white px-2 py-2 ${isDragging ? "relative z-10 rounded-xl shadow-lg ring-1 ring-slate-200" : ""}`}>
      <button type="button" {...attributes} {...listeners} className="cursor-grab touch-none rounded-lg p-1.5 text-slate-400 hover:bg-black/5 active:cursor-grabbing" aria-label={`Drag ${ev.title}`} data-testid={`live-order-handle-${ev.id}`}><GripVertical size={18} /></button>
      <span className="w-6 shrink-0 text-right text-xs tabular-nums text-slate-400">{n}</span>
      <RenameTitle ev={ev} onDone={onDone} className="flex-1 text-sm font-medium" />
      <span className="shrink-0 text-[11px] text-slate-400">{ev.status === "active" ? ev.category || "" : "concluded"}</span>
    </li>);
};

// Drag Live issues into the order members see. Issues with an update in the last 24 hours still show above the rest.
export const LiveOrder = ({ rows, onDone }) => {
  const [list, setList] = useState(() => byPosition(rows));
  useEffect(() => setList(byPosition(rows)), [rows]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const end = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(list, list.findIndex((x) => x.id === active.id), list.findIndex((x) => x.id === over.id));
    setList(next);
    try { await api.put("/admin/live/order", { ids: next.map((x) => x.id) }); toast.success("Order saved"); onDone(); } catch (e) { toast.error(errMsg(e)); setList(byPosition(rows)); }
  };
  return (
    <section className="surface space-y-2 p-5" data-testid="live-order">
      <h2 className="text-2xl">Order & names</h2>
      <p className="text-xs text-slate-500">Drag to set the order of Middle Live issues. Issues with an update in the last 24 hours still appear above the rest, in this same order. Tap a name to rename it.</p>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
        <SortableContext items={list.map((x) => x.id)} strategy={verticalListSortingStrategy}>
          <ol className="divide-y divide-border rounded-xl border border-border" data-testid="live-order-list">{list.map((ev, i) => <Row key={ev.id} ev={ev} n={i + 1} onDone={onDone} />)}</ol>
        </SortableContext>
      </DndContext>
    </section>
  );
};
